/**
 * Usernames are case-insensitive.
 *
 * Everything that writes a username already lowercases it first —
 * SignupPage.jsx, ReaderChooseUsernamePage.jsx, SettingsPage.jsx, and the
 * handle_new_reader_signup() trigger all do. What was missing is the reading
 * half: every lookup used .eq('username', …), which is case-sensitive in
 * Postgres, so /u/Emma returned "profile not found" for a reader stored as
 * emma. Phone keyboards capitalise the first letter, so that is not a rare
 * way to arrive at a profile URL.
 *
 * Why not .ilike(): underscore is a wildcard in LIKE, and usernames are
 * allowed to contain it. Looking up my_name with .ilike would also match
 * myXname and hand back the wrong person's profile. Every helper here does
 * exact matching on a lowercased value instead.
 *
 * The fallback query exists for rows written before any of this: if a legacy
 * profile is stored with capitals, the lowercased lookup misses it, so an
 * exact match on the value as typed is tried second. Once the migration in
 * this branch has normalised the table and added the unique index on
 * lower(username), the fallback stops ever finding anything — it is kept
 * because it costs one query only when the first one misses.
 */

import { supabase } from './supabase';

const MAX_LENGTH = 30;

/** Lowercase, drop anything that is not a-z, 0-9 or underscore, cap length. */
export function normalizeUsername(value) {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, MAX_LENGTH);
}

/**
 * Look a profile up by username, whatever the capitalisation.
 * Returns the row, or null.
 */
export async function findProfileByUsername(username, columns = '*') {
  const typed  = (username || '').trim();
  if (!typed) return null;
  const lower = typed.toLowerCase();

  const { data } = await supabase
    .from('user_profiles').select(columns).eq('username', lower).maybeSingle();
  if (data) return data;

  if (lower === typed) return null;

  const { data: legacy } = await supabase
    .from('user_profiles').select(columns).eq('username', typed).maybeSingle();
  return legacy || null;
}

/**
 * Is this username already somebody's?
 *
 * `excludeUserId` lets the settings page ignore the row belonging to the
 * person doing the editing, so saving your own unchanged username does not
 * report itself as taken.
 */
export async function isUsernameTaken(username, { excludeUserId = null } = {}) {
  const typed = (username || '').trim();
  if (!typed) return false;
  const lower = typed.toLowerCase();

  for (const value of lower === typed ? [lower] : [lower, typed]) {
    let query = supabase.from('user_profiles').select('user_id').eq('username', value);
    if (excludeUserId) query = query.neq('user_id', excludeUserId);
    const { data } = await query.maybeSingle();
    if (data) return true;
  }
  return false;
}

/**
 * Author handles live in a different table and are checked at signup so the
 * two portals cannot hand the same name to two people. Same casing rule.
 */
export async function isAuthorHandleTaken(handle) {
  const typed = (handle || '').trim();
  if (!typed) return false;
  const lower = typed.toLowerCase();

  for (const value of lower === typed ? [lower] : [lower, typed]) {
    const { data } = await supabase
      .from('author_profiles').select('id').eq('handle', value).maybeSingle();
    if (data) return true;
  }
  return false;
}
